import React, { useMemo, useEffect, useCallback, useRef } from 'react';
import { View, Text, ScrollView, Share } from 'react-native';
import { useRouter } from 'expo-router';
import {
  Share2,
  FileDown,
} from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { useProfileStore } from '@/lib/state/profile-store';
import { useScoreStore } from '@/lib/state/score-store';
import type { ScoreCriteria } from '@/lib/scoring/types';
import { useScoreHistoryStore, type ScoreSnapshot } from '@/lib/state/score-history-store';
import { useScoreHistory, useLatestScoreResult } from '@/lib/supabase/score-queries';
import { aiScoreToScoreCriteria } from '@/lib/scoring/ai-scoring';
import { mode } from '@/lib/config/app-mode';
import type { ScoreEventReadModel } from '@/lib/supabase/edge-functions';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { useResponsive } from '@/lib/useResponsive';
import { AppLayout } from '@/components/AppLayout';
import { ScreenFrame } from '@/components/ScreenFrame';
import AILauncherFAB from '@/components/AILauncherFAB';
import { TierGate, UpgradePrompt } from '@/components/subscription';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';
import AnimatedPressable from '@/components/AnimatedPressable';
import { AiTransparencyLabel } from '@/components/ai-transparency-label';
import {
  IRSScoreCard,
  DimensionCard,
  ScoreSectionDivider,
  SummaryCard,
  ScoreRadarChart,
  RiskAlertsSection,
  DealReadinessCard,
} from '@/components/score';
import {
  scoreToLabel,
  capitalizeLabel,
  computePillarSummaries,
  FUNDAMENTALS_DIMS,
  GROWTH_DIMS,
  evidenceLevelToSource,
  evidenceSourceMixFlavor,
  labelThemeColor,
} from '@/lib/scoring/score-display';
import { exportScorecardPdf } from '@/lib/scoring/export-scorecard-pdf';
import {
  dimensionLabels,
  buildSummaryStrengths,
  buildSummaryWeaknesses,
  buildScorecardExportPayload,
} from '@/lib/scoring/score-page-helpers';
import { useDataRoomStore, computeDealReadiness } from '@/lib/dataroom';
import { handleError } from '@/lib/errors';
import { useAlert } from '@/components/AlertDialog';

/**
 * Map a score_event read model to the ScoreSnapshot shape consumed by the
 * trend chart UI. Phase 116.6 — replaces the score_history-backed mapper
 * since score_history was retired in this phase.
 */
function scoreEventToSnapshot(event: ScoreEventReadModel): ScoreSnapshot {
  const finalScore = event.composite.finalScore;
  const readinessLevel = event.composite.ventureLabel;

  const dimensions = (event.dimensions ?? []).map((d) => ({
    dimension: d.id,
    score: d.rawScore,
    change: 0, // per-event deltas aren't precomputed; downstream renderers compute if needed
  }));

  return {
    id: event.scoreEventId,
    timestamp: new Date(event.computedAt),
    finalScore,
    dimensions,
    trigger: {
      type: 'manual' as const,
      documentName: 'AI Score',
    },
    readinessLevel,
  };
}


export default function ScoreScreen() {
  useTrackScreen('Score');
  const scoreTracked = useRef(false);
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  const scoreCriteria = useScoreStore((s) => s.scoreCriteria);
  const summaryNarrative = useScoreStore((s) => s.summaryNarrative);
  const loadSampleScores = useScoreStore((s) => s.loadSampleScores);
  const setScoreCriteria = useScoreStore((s) => s.setScoreCriteria);
  const startupProfile = useProfileStore((s) => s.startupProfile);
  const startupName = startupProfile?.name ?? 'Your Startup';

  // Score history — DB-backed (primary) with client store fallback
  const clientSnapshots = useScoreHistoryStore((s) => s.snapshots);
  const lastScoredAt = useScoreHistoryStore((s) => s.lastScoredAt);
  const getScoreChange = useScoreHistoryStore((s) => s.getScoreChange);
  const getTotalChange = useScoreHistoryStore((s) => s.getTotalChange);
  const getRecentChanges = useScoreHistoryStore((s) => s.getRecentChanges);

  // DB-backed score history query
  const startupId = startupProfile?.id ?? '';
  const dbHistory = useScoreHistory(startupId);
  // The single source of truth for the headline + dimension breakdown: the
  // latest real score_event produced by the scoring engine. Never the client
  // heuristic (which fabricated a ~50 for unscored companies).
  const latestScore = useLatestScoreResult(startupId);
  const scoreSnapshots = useMemo(() => {
    if (dbHistory.data && dbHistory.data.length > 0) {
      return dbHistory.data.map(scoreEventToSnapshot);
    }
    return clientSnapshots;
  }, [dbHistory.data, clientSnapshots]);

  // Track score_viewed on mount
  useEffect(() => {
    if (scoreTracked.current) return;
    scoreTracked.current = true;
    track('score_viewed');
  }, []);

  // Hydrate the score display from the REAL scoring engine (score_event).
  // Never fabricate a client-side score from profile fields for a company the
  // engine has not scored — an unscored company must show the honest empty
  // state (the CTA cards), not a heuristic number that drifts toward 50.
  useEffect(() => {
    if (latestScore.data) {
      // Real engine score exists → render its dimensions.
      setScoreCriteria(aiScoreToScoreCriteria(latestScore.data.dimensions));
    } else if (mode.showSampleData) {
      // Demo/sample mode only → illustrative sample scores.
      loadSampleScores();
    } else if (!latestScore.isLoading) {
      // Production, no real score → honest empty state (CTA cards).
      setScoreCriteria([]);
    }
  }, [latestScore.data, latestScore.isLoading, setScoreCriteria, loadSampleScores]);

  // Feature gating - full 15-dimension report requires Starter+
  const hasFullReport = useSubscriptionStore((s) => s.hasFeature('full_dimension_report'));

  // ── Derived Score Data ───────────────────────────────────────────────────────

  // Overall Score (points out of 100). Prefer the engine's weighted composite
  // (the same number the investor sees) over a client-side average of dims.
  const overallScore = useMemo(() => {
    if (latestScore.data) return Math.round(latestScore.data.composite.finalScore);
    if (scoreCriteria.length === 0) return 0;
    const total = scoreCriteria.reduce((sum, c) => sum + c.score, 0);
    const maxTotal = scoreCriteria.reduce((sum, c) => sum + c.maxScore, 0);
    return maxTotal > 0 ? Math.round((total / maxTotal) * 100) : 0;
  }, [latestScore.data, scoreCriteria]);

  // Confidence Score — weighted average of v3 evidence tier multipliers
  const confidenceScore = useMemo(() => {
    if (scoreCriteria.length === 0) return 0;
    const evidenceMultiplier = (level: string): number => {
      if (level === 'verified') return 1.0;
      if (level === 'partially-verified') return 0.75;
      return 0.5; // unverified, missing, or legacy values
    };
    const totalConfidence = scoreCriteria.reduce(
      (sum, c) => sum + evidenceMultiplier(c.evidenceLevel), 0,
    );
    return Math.round((totalConfidence / scoreCriteria.length) * 100);
  }, [scoreCriteria]);

  // Confidence multiplier (0-1 range) — trivial derivation, no memo needed
  const confidenceMultiplier = confidenceScore / 100;

  // Has-score flag (used by downstream useMemos)
  const hasScore = overallScore > 0 || scoreCriteria.length > 0;

  // Aggregate confidence — alias for the existing confidenceScore. Will become
  // a weighted average of per-dimension confidence once the LLM emits it.
  const aggregateConfidence = confidenceScore;

  // Source-mix flavor — derived from per-dimension evidence levels
  const sourceFlavor = useMemo(() => {
    if (!hasScore || scoreCriteria.length === 0) return undefined;
    const sources = scoreCriteria.map((c) => evidenceLevelToSource(c.evidenceLevel, !!c.feedback));
    return evidenceSourceMixFlavor(sources);
  }, [hasScore, scoreCriteria]);

  // Risk Summary
  const riskSummary = useMemo(() => {
    const high = scoreCriteria.filter((c) => c.riskLevel === 'high');
    const medium = scoreCriteria.filter((c) => c.riskLevel === 'medium');
    const low = scoreCriteria.filter((c) => c.riskLevel === 'low');
    return {
      high: high.length,
      medium: medium.length,
      low: low.length,
      highItems: high,
      mediumItems: medium,
    };
  }, [scoreCriteria]);

  // Risk summary text for IRSScoreCard
  const riskSummaryText = useMemo(() => {
    const parts: string[] = [];
    if (riskSummary.high > 0) parts.push(`${riskSummary.high} High`);
    if (riskSummary.medium > 0) parts.push(`${riskSummary.medium} Med`);
    if (parts.length === 0) return 'Low';
    return parts.join(' \u00B7 ');
  }, [riskSummary]);

  // Evidence label for IRSScoreCard — trivial branch, no memo needed
  const evidenceLabel: 'High' | 'Moderate' | 'Low' =
    confidenceMultiplier > 0.85 ? 'High' : confidenceMultiplier >= 0.65 ? 'Moderate' : 'Low';

  // Venture label from score — trivial derivation, no memo needed
  const ventureLabel =
    overallScore === 0 && scoreCriteria.length === 0 ? undefined : scoreToLabel(overallScore);

  // Summary card: top strengths and key weaknesses
  const summaryStrengths = useMemo(
    () => buildSummaryStrengths(scoreCriteria),
    [scoreCriteria],
  );
  const summaryWeaknesses = useMemo(
    () => buildSummaryWeaknesses(scoreCriteria),
    [scoreCriteria],
  );

  // Deal Readiness — REAL, from the startup's own data-room checklist uploads.
  // (Replaces the prior MOCK_DD_DOCS + dimension-coverage estimate.) Drives both
  // the IRSScoreCard tile and the Deal Readiness checklist + PDF export.
  const checklistUploads = useDataRoomStore((s) => s.uploads);
  const checklistStage = useDataRoomStore((s) => s.currentStage);
  const ddStats = useMemo(
    () => computeDealReadiness(checklistUploads, checklistStage),
    [checklistUploads, checklistStage],
  );
  const dealReadiness = ddStats;

  // ── Dimension-level data ─────────────────────────────────────────────────────

  // Filter criteria with dimensions
  const dimensionCriteria = useMemo(() => {
    return scoreCriteria.filter((c) => c.dimension);
  }, [scoreCriteria]);

  // Build risks array for RiskAlertsSection
  const risks = useMemo(() => {
    return scoreCriteria
      .filter((c) => c.riskLevel === 'high' || c.riskLevel === 'medium')
      .map((c) => ({
        category: dimensionLabels[c.dimension!] || c.name,
        description: c.verificationNote || c.feedback || `${dimensionLabels[c.dimension!] || c.name} flagged as ${c.riskLevel} risk`,
        severity: c.riskLevel,
        mitigation: c.recommendation,
      }));
  }, [scoreCriteria]);

  // Build recommendations array
  const recommendations = useMemo(() => {
    return scoreCriteria
      .filter((c) => c.recommendation)
      .sort((a, b) => {
        // Sort by risk level (high first) then by score (low first)
        const riskOrder = { high: 0, medium: 1, low: 2, none: 3 };
        const riskDiff = (riskOrder[a.riskLevel] ?? 3) - (riskOrder[b.riskLevel] ?? 3);
        if (riskDiff !== 0) return riskDiff;
        return a.score - b.score;
      })
      .map((c) => c.recommendation!)
      .slice(0, 5);
  }, [scoreCriteria]);

  // Pillar summaries — map scoreCriteria to the format computePillarSummaries expects
  const pillarSummaries = useMemo(() => {
    const dimsForPillar = dimensionCriteria.map((c) => ({
      dimension: c.dimension!,
      rawScore: Math.round((c.score / c.maxScore) * 100),
    }));
    return computePillarSummaries(dimsForPillar);
  }, [dimensionCriteria]);

  // Split dimensions by pillar
  const fundamentalsDims = useMemo(() => {
    return dimensionCriteria.filter((c) =>
      (FUNDAMENTALS_DIMS as readonly string[]).includes(c.dimension!),
    );
  }, [dimensionCriteria]);

  const growthDims = useMemo(() => {
    return dimensionCriteria.filter((c) =>
      (GROWTH_DIMS as readonly string[]).includes(c.dimension!),
    );
  }, [dimensionCriteria]);

  // ── Helper: find matching risk for a dimension ───────────────────────────────
  const getDimensionRisk = useCallback((criterion: ScoreCriteria) => {
    if (criterion.riskLevel !== 'high' && criterion.riskLevel !== 'medium') return undefined;
    return {
      description: criterion.verificationNote || criterion.feedback || `${dimensionLabels[criterion.dimension!] || criterion.name} requires attention`,
      severity: criterion.riskLevel,
      mitigation: criterion.recommendation,
    };
  }, []);

  // ── Share Handler ────────────────────────────────────────────────────────────

  const handleShareScore = async () => {
    try {
      await Share.share({
        message:
          `${startupName} Investment Readiness Score\n\n` +
          `Overall Score: ${overallScore}/100 - ${ventureLabel ? capitalizeLabel(ventureLabel) : 'Not scored'}\n` +
          `Evidence Quality: ${evidenceLabel}\n` +
          `Risk Summary: ${riskSummaryText}\n\n` +
          `Scored by Inverio - AI-powered investment readiness platform\n` +
          `Learn more at inverio.ai`,
        title: `${startupName} - Investment Readiness Score`,
      });
    } catch (_error) {
      // Share cancelled or failed
    }
  };

  const { isDesktop } = useResponsive();
  const hasDimensions = dimensionCriteria.length > 0;
  const alert = useAlert();

  // ── Export PDF handler ─────────────────────────────────────────────────────

  const handleExportPdf = useCallback(async () => {
    try {
      const payload = buildScorecardExportPayload({
        startupName: startupProfile?.name ?? 'Your Startup',
        tagline: startupProfile?.tagline,
        description: startupProfile?.description,
        finalScore: overallScore,
        ventureLabel: ventureLabel ?? '—',
        evidenceConfidence: confidenceScore,
        sourceFlavor,
        riskSummary,
        summaryNarrative,
        summaryStrengths,
        summaryWeaknesses,
        recommendations,
        risks,
        fundamentalsCriteria: fundamentalsDims,
        growthCriteria: growthDims,
        dealReadiness: ddStats,
      });
      await exportScorecardPdf(payload);
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'score.handleExportPdf' });
      if (!isCancelled) alert.alert('Export failed', message);
    }
  }, [
    startupProfile,
    overallScore,
    ventureLabel,
    confidenceScore,
    sourceFlavor,
    riskSummary,
    summaryNarrative,
    summaryStrengths,
    summaryWeaknesses,
    recommendations,
    risks,
    fundamentalsDims,
    growthDims,
    ddStats,
    alert,
  ]);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <AppLayout title="Score Breakdown" showSearch>
      {/* PINNED, and carrying the global controls. Profile left the tab bar for
         the avatar, so every primary page has to offer it — otherwise account,
         settings and help are reachable only by going Home and scrolling to the
         top. The header also stops scrolling away: it used to live inside the
         ScrollView below. Share stays as this page's contextual action, now at
         the 44pt target. No bottomEdge — this is a tab screen. */}
      <ScreenFrame
        eyebrow="Investment Readiness"
        title={startupName}
        actions={
          <>
            <AnimatedPressable
              onPress={handleShareScore}
              accessibilityRole="button"
              accessibilityLabel="Share score"
              style={{
                width: 44, height: 44, borderRadius: 22,
                alignItems: 'center', justifyContent: 'center',
                backgroundColor: colors.primary[50],
              }}
            >
              <Share2 size={20} color={colors.primary[600]} />
            </AnimatedPressable>
            <ScreenFrame.GlobalActions />
          </>
        }
      >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>

            {/* ── Top-right action row: Export Report (when scored) ──────────── */}
            {hasScore && (
              <Animated.View
                entering={FadeInUp.delay(180).duration(400)}
                className="px-5 mb-3"
                style={{ flexDirection: 'row', justifyContent: 'flex-end' }}
              >
                <AnimatedPressable
                  onPress={handleExportPdf}
                  style={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    paddingHorizontal: 12,
                    paddingVertical: 7,
                    borderRadius: 8,
                    backgroundColor: colors.surface.primary,
                    borderWidth: 1,
                    borderColor: colors.border.medium,
                  }}
                  accessibilityRole="button"
                  accessibilityLabel="Download full report as PDF"
                >
                  <FileDown size={14} color={colors.text.secondary} />
                  <Text style={{ color: colors.text.secondary, fontSize: 12, fontWeight: '600' }} numberOfLines={1}>
                    Download Report
                  </Text>
                </AnimatedPressable>
              </Animated.View>
            )}

            {/* ── 1. IRS Score Card ─────────────────────────────────────────── */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-4"
            >
              <IRSScoreCard
                companyName={startupProfile?.name}
                tagline={startupProfile?.tagline}
                description={startupProfile?.about}
                finalScore={hasScore ? overallScore : undefined}
                ventureLabel={ventureLabel}
                ventureScore={hasScore ? overallScore : undefined}
                evidenceLabel={hasScore ? evidenceLabel : undefined}
                evidenceConfidence={hasScore ? aggregateConfidence : undefined}
                evidenceSourceFlavor={hasScore ? sourceFlavor : undefined}
                dealReadinessPercent={dealReadiness?.percent}
                dealReadinessPresent={dealReadiness?.present}
                dealReadinessTotal={dealReadiness?.total}
                riskSummary={hasScore ? riskSummary : undefined}
                onUploadDeck={() => router.push('/(onboarding)/pitch-deck-upload')}
                onConnectDataRoom={() => router.push('/(onboarding)/data-room-connect')}
                onScoringInterview={() => router.push('/(startup)/interview')}
                onShare={hasScore ? handleShareScore : undefined}
              />
              {hasScore && (
                <View className="mt-2">
                  <UsageIndicator feature="pitch_deck_analysis" suffix="/mo" />
                </View>
              )}
            </Animated.View>

            {/* ── 2. Fundamentals Pillar — Gated for Starter+ ───────────────── */}
            <TierGate
              feature="full_dimension_report"
              fallback={
                <View className="px-5 mb-4">
                  <UpgradePrompt
                    targetTier="starter"
                    variant="card"
                    headline="Unlock Full Score Breakdown"
                    lockedTitle="Full score breakdown"
                    maxFeatures={3}
                  />
                </View>
              }
            >
              {hasDimensions && (
                <View className="px-5">
                  <ScoreSectionDivider title="Executive Summary" label={ventureLabel} />
                  <SummaryCard
                    companyName={startupProfile?.name}
                    finalScore={overallScore}
                    ventureLabel={ventureLabel}
                    strengths={summaryStrengths}
                    weaknesses={summaryWeaknesses}
                    recommendations={recommendations}
                    risks={risks}
                    narrative={summaryNarrative ?? undefined}
                  />
                  {/* ── Venture Quality overview — two radar cards side-by-side ── */}
                  <ScoreSectionDivider
                    title="Venture Quality"
                    label={ventureLabel}
                    score={overallScore}
                  />
                  <View
                    style={{
                      flexDirection: isWeb ? 'row' : 'column',
                      gap: 12,
                      marginBottom: 4,
                    }}
                  >
                    {/* Fundamentals radar card */}
                    <View
                      style={{
                        flex: 1,
                        backgroundColor: colors.surface.primary,
                        borderRadius: 14,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                        padding: 14,
                        alignItems: 'center',
                      }}
                    >
                      <View
                        style={{
                          width: '100%',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                        }}
                      >
                        <Text
                          style={{
                            color: colors.text.tertiary,
                            fontSize: 9,
                            fontWeight: '700',
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                          }}
                        >
                          Fundamentals
                        </Text>
                        <Text
                          style={{
                            color: labelThemeColor(pillarSummaries.fundamentals.label, colors),
                            fontSize: 12,
                            fontWeight: '700',
                          }}
                        >
                          {pillarSummaries.fundamentals.averageScore}
                        </Text>
                      </View>
                      <ScoreRadarChart
                        size={isWeb ? 340 : 280}
                        showScoresInLabels
                        color={labelThemeColor(pillarSummaries.fundamentals.label, colors)}
                        dimensions={fundamentalsDims.map((c) => ({
                          name: dimensionLabels[c.dimension!] || c.name,
                          score: Math.round((c.score / c.maxScore) * 100),
                        }))}
                      />
                    </View>

                    {/* Growth & Execution radar card */}
                    <View
                      style={{
                        flex: 1,
                        backgroundColor: colors.surface.primary,
                        borderRadius: 14,
                        borderWidth: 1,
                        borderColor: colors.border.light,
                        padding: 14,
                        alignItems: 'center',
                      }}
                    >
                      <View
                        style={{
                          width: '100%',
                          flexDirection: 'row',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          marginBottom: 8,
                        }}
                      >
                        <Text
                          style={{
                            color: colors.text.tertiary,
                            fontSize: 9,
                            fontWeight: '700',
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                          }}
                        >
                          Growth & Execution
                        </Text>
                        <Text
                          style={{
                            color: labelThemeColor(pillarSummaries.growth.label, colors),
                            fontSize: 12,
                            fontWeight: '700',
                          }}
                        >
                          {pillarSummaries.growth.averageScore}
                        </Text>
                      </View>
                      <ScoreRadarChart
                        size={isWeb ? 340 : 280}
                        showScoresInLabels
                        color={labelThemeColor(pillarSummaries.growth.label, colors)}
                        dimensions={growthDims.map((c) => ({
                          name: dimensionLabels[c.dimension!] || c.name,
                          score: Math.round((c.score / c.maxScore) * 100),
                        }))}
                      />
                    </View>
                  </View>

                  <ScoreSectionDivider
                    title="Fundamentals"
                    label={pillarSummaries.fundamentals.label}
                    score={pillarSummaries.fundamentals.averageScore}
                  />
                  <View
                    style={isWeb ? {
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 12,
                    } : undefined}
                  >
                    {fundamentalsDims.map((criterion) => {
                      const dimScore = Math.round((criterion.score / criterion.maxScore) * 100);
                      return (
                        <View
                          key={criterion.id}
                          style={isWeb ? { width: '48%', minWidth: 300 } : undefined}
                        >
                          <DimensionCard
                            name={dimensionLabels[criterion.dimension!] || criterion.name}
                            rawScore={dimScore}
                            feedback={criterion.feedback || ''}
                            evidenceLevel={criterion.evidenceLevel}
                            confidence={criterion.confidence}
                            riskLevel={criterion.riskLevel}
                            riskCounts={
                              criterion.riskLevel === 'high'
                                ? { high: 1, medium: 0, low: 0 }
                                : criterion.riskLevel === 'medium'
                                  ? { high: 0, medium: 1, low: 0 }
                                  : criterion.riskLevel === 'low'
                                    ? { high: 0, medium: 0, low: 1 }
                                    : { high: 0, medium: 0, low: 0 }
                            }
                            role="startup"
                            improvementTip={criterion.recommendation}
                            dimensionRisk={getDimensionRisk(criterion)}
                          />
                        </View>
                      );
                    })}
                    {fundamentalsDims.length === 0 && (
                      <Text
                        style={{ color: colors.text.tertiary, fontStyle: 'italic', fontSize: 13, paddingVertical: 8 }}
                        numberOfLines={2}
                      >
                        Upload documents to generate scores for Fundamentals.
                      </Text>
                    )}
                  </View>

                  {/* ── 5. Growth & Execution Pillar ──────────────────────────── */}
                  <ScoreSectionDivider
                    title="Growth & Execution"
                    label={pillarSummaries.growth.label}
                    score={pillarSummaries.growth.averageScore}
                  />
                  <View
                    style={isWeb ? {
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 12,
                    } : undefined}
                  >
                    {growthDims.map((criterion) => {
                      const dimScore = Math.round((criterion.score / criterion.maxScore) * 100);
                      return (
                        <View
                          key={criterion.id}
                          style={isWeb ? { width: '48%', minWidth: 300 } : undefined}
                        >
                          <DimensionCard
                            name={dimensionLabels[criterion.dimension!] || criterion.name}
                            rawScore={dimScore}
                            feedback={criterion.feedback || ''}
                            evidenceLevel={criterion.evidenceLevel}
                            confidence={criterion.confidence}
                            riskLevel={criterion.riskLevel}
                            riskCounts={
                              criterion.riskLevel === 'high'
                                ? { high: 1, medium: 0, low: 0 }
                                : criterion.riskLevel === 'medium'
                                  ? { high: 0, medium: 1, low: 0 }
                                  : criterion.riskLevel === 'low'
                                    ? { high: 0, medium: 0, low: 1 }
                                    : { high: 0, medium: 0, low: 0 }
                            }
                            role="startup"
                            improvementTip={criterion.recommendation}
                            dimensionRisk={getDimensionRisk(criterion)}
                          />
                        </View>
                      );
                    })}
                    {growthDims.length === 0 && (
                      <Text
                        style={{ color: colors.text.tertiary, fontStyle: 'italic', fontSize: 13, paddingVertical: 8 }}
                        numberOfLines={2}
                      >
                        Upload documents to generate scores for Growth & Execution.
                      </Text>
                    )}
                  </View>

                  {/* ── 6. Deal Readiness — checklist of investment documents ───── */}
                  {(() => {
                    const dd = ddStats;
                    return (
                      <>
                        <ScoreSectionDivider
                          title="Deal Readiness"
                          score={`${dd.present}/${dd.total}`}
                        />
                        <DealReadinessCard
                          present={dd.present}
                          total={dd.total}
                          missingCritical={dd.missingCritical}
                          missingRecommended={dd.missingRecommended}
                        />
                      </>
                    );
                  })()}
                </View>
              )}

              {/* Empty dimensions state within TierGate */}
              {!hasDimensions && hasScore && (
                <View className="px-5 mb-4">
                  <Text
                    style={{ color: colors.text.tertiary, fontStyle: 'italic', fontSize: 13, paddingVertical: 8 }}
                    numberOfLines={2}
                  >
                    Upload a pitch deck or connect your data room to see the full dimension breakdown.
                  </Text>
                </View>
              )}
            </TierGate>

            {/* ── 7. AI Transparency Footer ─────────────────────────────────── */}
            {hasScore && (
              <View className="px-5 mt-4 mb-6">
                <AiTransparencyLabel variant="banner" context="scoring" />
              </View>
            )}

            {/* Bottom padding */}
            <View className="pb-8" />
          </ScrollView>

          <AILauncherFAB targetRoute="/(startup)/insights" flagKey="ai_coach" />
      </ScreenFrame>
    </AppLayout>
  );
}
